
import { useState } from "react";
import { ChevronDown, ChevronRight, PanelRightOpen } from "lucide-react";

/** Editing token for a source-file column: `/Name`. */
export function sourceColumnCommand(column: string): string {
  return `/${column}`;
}

/**
 * Editing token for a workbook (export) column: `/out:Name` — the FieldInput
 * syntax for a processed-field reference (see `fieldInputText.ts`). A bare
 * `/Name` would insert a SOURCE reference instead.
 */
export function exportColumnCommand(column: string): string {
  return `/out:${column}`;
}

/**
 * Pure list of columns — clicking one inserts its token via `tokenize`. The
 * enclosing section header says whether these are source or export fields,
 * so each row is just the column name (full width, truncated, full text in
 * the tooltip).
 */
export function SourceColumnList({
  columns,
  onInsert,
  tokenize = sourceColumnCommand,
  display = sourceColumnCommand,
}: {
  columns: string[];
  onInsert: (token: string) => void;
  tokenize?: (column: string) => string;
  display?: (column: string) => string;
}) {
  if (columns.length === 0) {
    return (
      <div className="px-2 py-6 text-center text-[12px] text-slate-500 dark:text-slate-400">
        No columns
      </div>
    );
  }
  return (
    <div className="space-y-1">
      {columns.map((column, index) => (
        <button
          key={`${column}:${index}`}
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onInsert(tokenize(column))}
          title={`Insert ${display(column)}`}
          className="flex w-full min-w-0 items-center rounded px-2 py-1.5 text-left font-mono text-[11.5px] text-slate-700 transition hover:bg-slate-100 focus:bg-slate-100 focus:outline-none dark:text-slate-300 dark:hover:bg-surface-2 dark:focus:bg-surface-2"
        >
          <span className="min-w-0 flex-1 truncate" title={column}>
            {column}
          </span>
        </button>
      ))}
    </div>
  );
}

export const FIELDS_PANEL_OPEN_WIDTH = 224;
export const FIELDS_PANEL_RAIL_WIDTH = 36;

/** Collapsible section within the Fields panel. */
function FieldSection({
  title,
  count,
  hint,
  children,
}: {
  title: string;
  count: number;
  hint: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);

  return (
    <div className="border-b border-slate-100 dark:border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1 px-2 py-1.5 text-left transition hover:bg-slate-50 dark:hover:bg-surface-2"
      >
        <ChevronDown
          className={[
            "h-3 w-3 shrink-0 text-slate-400 dark:text-slate-500 transition-transform",
            open ? "" : "-rotate-90",
          ].join(" ")}
        />
        <span className="flex-1 text-[11px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
          {title}
        </span>
        <span className="rounded-full bg-slate-100 dark:bg-surface-2 px-1.5 text-[9px] font-semibold text-slate-500 dark:text-slate-400">
          {count}
        </span>
      </button>
      {open && (
        <>
          <div className="px-3 pb-1.5 text-[10.5px] text-slate-400 dark:text-slate-500">
            {hint}
          </div>
          <div className="p-2 pt-0">
            {children}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Right-edge collapsible panel with two stacked sections:
 *  - Source fields → insert a source column token into the open field editor
 *  - Export fields → insert a workbook/output column token
 * `open` is controlled by the host so it can size the floating module drawer to
 * match the panel's real width (rail when collapsed, full when open).
 */
export function FieldsPanel({
  sourceColumns,
  exportColumns,
  onInsert,
  open,
  onToggle,
}: {
  sourceColumns: string[];
  exportColumns: string[];
  onInsert: (token: string) => void;
  open: boolean;
  onToggle: (open: boolean) => void;
}) {
  if (!open) {
    return (
      <aside className="flex w-9 shrink-0 flex-col items-center border-l border-slate-200 bg-white py-2 dark:border-border dark:bg-surface">
        <button
          type="button"
          onClick={() => onToggle(true)}
          title="Show source / export fields"
          aria-label="Show fields panel"
          className="flex flex-col items-center gap-2 rounded px-1.5 py-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-surface-2 dark:hover:text-slate-200"
        >
          <PanelRightOpen className="h-4 w-4" />
          <span className="text-[10px] font-semibold uppercase tracking-wider [writing-mode:vertical-rl]">
            Fields
          </span>
        </button>
      </aside>
    );
  }

  return (
    <aside className="flex w-56 shrink-0 flex-col border-l border-slate-200 bg-white dark:border-border dark:bg-surface">
      <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-2 py-1.5 dark:border-border">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
          Fields
        </span>
        <button
          type="button"
          onClick={() => onToggle(false)}
          title="Collapse fields panel"
          aria-label="Collapse fields panel"
          className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-surface-2 dark:hover:text-slate-300"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <FieldSection
          title="Source fields"
          count={sourceColumns.length}
          hint="Click to insert a / field from your source file."
        >
          <SourceColumnList
            columns={sourceColumns}
            onInsert={onInsert}
            tokenize={sourceColumnCommand}
            display={sourceColumnCommand}
          />
        </FieldSection>
        <FieldSection
          title="Export fields"
          count={exportColumns.length}
          hint="Click to insert a / field from the processed workbook."
        >
          <SourceColumnList
            columns={exportColumns}
            onInsert={onInsert}
            tokenize={exportColumnCommand}
            display={exportColumnCommand}
          />
        </FieldSection>
      </div>
    </aside>
  );
}
